import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useRouteError } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Button } from '@/components/custom/button'
import { Loader } from '@/components/custom/loader'
import { cn } from '@/lib/utils'

interface GeneralErrorProps extends React.HTMLAttributes<HTMLDivElement> {
  minimal?: boolean
}

// Messages browsers throw when a lazy-loaded chunk 404s because it was removed
// from the server by a newer deploy while this tab was still open.
const STALE_CHUNK_ERROR_PATTERN =
  /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i

const STALE_CHUNK_RELOAD_KEY = 'aros-dashboard:stale-chunk-reload-at'
// If a reload was attempted this recently, treat a repeat failure as a real
// error instead of retrying again (avoids an infinite reload loop).
const STALE_CHUNK_RELOAD_WINDOW_MS = 10000

const isStaleChunkError = (error: unknown): boolean => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : ''
  return STALE_CHUNK_ERROR_PATTERN.test(message)
}

const hasRecentlyRetried = (): boolean => {
  const lastAttempt = sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)
  if (!lastAttempt) return false
  return Date.now() - Number(lastAttempt) < STALE_CHUNK_RELOAD_WINDOW_MS
}

export default function GeneralError({
  className,
  minimal = false,
}: GeneralErrorProps) {
  const navigate = useNavigate()
  const error = useRouteError()
  const { t } = useTranslation(['global'])

  const staleChunk = isStaleChunkError(error)
  // Computed once per mount so the value stays stable even after the effect
  // below writes the guard flag.
  const alreadyRetried = useMemo(() => hasRecentlyRetried(), [])
  const willRetry = staleChunk && !alreadyRetried

  // Reload once, at the target location the router already committed to.
  useEffect(() => {
    if (!willRetry) return
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()))
    window.location.reload()
  }, [willRetry])

  // Only report real errors — not the transient stale-chunk case we're about
  // to silently recover from.
  useEffect(() => {
    if (willRetry) return
    Sentry.captureException(error)
  }, [error, willRetry])

  if (willRetry) {
    return (
      <div className={cn('h-svh w-full', className)}>
        <div className='m-auto flex h-full w-full flex-col items-center justify-center gap-2'>
          <Loader text={t('errors.stale_chunk_reloading')} />
        </div>
      </div>
    )
  }

  return (
    <div className={cn('h-svh w-full', className)}>
      <div className='m-auto flex h-full w-full flex-col items-center justify-center gap-2'>
        {!minimal && (
          <h1 className='text-[7rem] font-bold leading-tight'>500</h1>
        )}
        <span className='font-medium'>Oops! Something went wrong {`:')`}</span>
        <p className='text-center text-muted-foreground'>
          We apologize for the inconvenience. <br /> Please try again later.
        </p>
        {!minimal && (
          <div className='mt-6 flex gap-4'>
            <Button variant='outline' onClick={() => navigate(-1)}>
              Go Back
            </Button>
            <Button onClick={() => navigate('/')}>Back to Home</Button>
          </div>
        )}
      </div>
    </div>
  )
}
